import SpocDetails from "../components/POC/POC"
import AccountLocation from "../components/AccountLocation"
import ChargerDetails from "../components/ChargerDetails"
import DealDetails from "../components/DealDetails"
import MaterialDispatch from "../components/MaterialDispatch"
import PaymentDetails from "../components/PaymentDetails"
import ProjectDetails from "../components/ProjectDetails"
import SoloSurvey from "../components/SoloSurvey/SoloSurvey"
import { BASE_STEPS, DEAL_TEMPLATES, DEAL_TYPE, FORM_STEPS, SCOPE_OF_WORK } from "./projectFormConstants"
import Infra from "../components/Infra/Infra"
import SaaS from "components/ProjectForm/pages/Saas"

/**
 * Each form step has a component to render and a title to display
 */
export type StepDefinition = {
    component: React.ComponentType<any>
    title: string
}

/**
 * Type-safe definition for STEPS_CONFIG
 */
type StepsConfig = {
    [K in DEAL_TEMPLATES]: FORM_STEPS[]
}

/**
 * Mapping of deal templates to their respective scope of work.
 * Used to set the values for the scope of work based on the deal template and vice-versa.
 */
export const SOW_MAPPING: Record<DEAL_TEMPLATES, SCOPE_OF_WORK[]> = {
    [DEAL_TEMPLATES.CHARGER_DELIVERY]: [SCOPE_OF_WORK.CHARGER],
    [DEAL_TEMPLATES.CHARGER_INFRA]: [SCOPE_OF_WORK.CHARGER, SCOPE_OF_WORK.INFRA],
    [DEAL_TEMPLATES.CHARGER_INFRA_SAAS]: [SCOPE_OF_WORK.CHARGER, SCOPE_OF_WORK.INFRA, SCOPE_OF_WORK.SAAS],
    // [DEAL_TEMPLATES.CHARGER_SOLO_SURVEY]: [SCOPE_OF_WORK.CHARGER, SCOPE_OF_WORK.SOLO_SURVEY],
    // [DEAL_TEMPLATES.CHARGER_SOLO_LOGISTICS]: [SCOPE_OF_WORK.CHARGER, SCOPE_OF_WORK.SOLO_LOGISTICS],
    [DEAL_TEMPLATES.INFRA_SAAS]: [SCOPE_OF_WORK.INFRA, SCOPE_OF_WORK.SAAS],
    [DEAL_TEMPLATES.INFRA]: [SCOPE_OF_WORK.INFRA],
    [DEAL_TEMPLATES.SOLO_SURVEY]: [SCOPE_OF_WORK.SOLO_SURVEY],
    [DEAL_TEMPLATES.SOLO_LOGISTICS]: [SCOPE_OF_WORK.SOLO_LOGISTICS],
    [DEAL_TEMPLATES.DEMOLITION]: [SCOPE_OF_WORK.DEMOLITION],
    [DEAL_TEMPLATES.EV_INFRA]: [SCOPE_OF_WORK.INFRA],
}

/**
 * A mapping from each FORM_STEP to its component and title
 */
export const FormSteps: Record<FORM_STEPS, StepDefinition> = {
    [FORM_STEPS.DEAL_SOW]: {
        component: DealDetails,
        title: "Deal & SOW",
    },
    [FORM_STEPS.PROJECT_DETAILS]: {
        component: ProjectDetails,
        title: "Project Details",
    },
    [FORM_STEPS.ACCOUNT_N_LOCATION]: {
        component: AccountLocation,
        title: "Location",
    },
    [FORM_STEPS.CHARGER_DETAILS]: {
        component: ChargerDetails,
        title: "Charger Details",
    },
    [FORM_STEPS.MATERIAL_DISPATCH]: {
        component: MaterialDispatch,
        title: "Material Dispatch",
    },
    [FORM_STEPS.SURVEY]: {
        component: SoloSurvey,
        title: "Survey",
    },
    [FORM_STEPS.INFRA]: {
        component: Infra,
        title: "Infrastructure",
    },
    [FORM_STEPS.SAAS]: {
        component: SaaS,
        title: "SaaS",
    },
    [FORM_STEPS.PAYMENT_DETAILS]: {
        component: PaymentDetails,
        title: "Payment Details",
    },
    [FORM_STEPS.SPOC_DETAILS]: {
        component: SpocDetails,
        title: "SPOC Details",
    },
}

/**
 * Builds a list of steps by combining the base steps with any additional steps provided
 * @param additionalSteps - Additional steps to include in the final list
 * @returns A list of steps
 */
function buildSteps(...additionalSteps: FORM_STEPS[]): FORM_STEPS[] {
    return [...BASE_STEPS, ...additionalSteps]
}

/**
 * Mapping of deal templates to their respective steps
 * The order in each array determines the order of the form flow
 */
export const STEPS_CONFIG: StepsConfig = {
    [DEAL_TEMPLATES.CHARGER_DELIVERY]: buildSteps(FORM_STEPS.PAYMENT_DETAILS, FORM_STEPS.SPOC_DETAILS),
    [DEAL_TEMPLATES.CHARGER_INFRA]: buildSteps(FORM_STEPS.INFRA, FORM_STEPS.PAYMENT_DETAILS, FORM_STEPS.SPOC_DETAILS),
    [DEAL_TEMPLATES.CHARGER_INFRA_SAAS]: buildSteps(
        FORM_STEPS.INFRA,
        FORM_STEPS.SAAS,
        FORM_STEPS.PAYMENT_DETAILS,
        FORM_STEPS.SPOC_DETAILS
    ),
    // [DEAL_TEMPLATES.CHARGER_SOLO_LOGISTICS]: buildSteps(
    //     FORM_STEPS.INFRA,
    //     FORM_STEPS.SAAS,
    //     FORM_STEPS.PAYMENT_DETAILS,
    //     FORM_STEPS.SPOC_DETAILS
    // ),
    // [DEAL_TEMPLATES.CHARGER_SOLO_SURVEY]: buildSteps(
    //     FORM_STEPS.SURVEY,
    //     FORM_STEPS.PAYMENT_DETAILS,
    //     FORM_STEPS.SPOC_DETAILS
    // ),
    [DEAL_TEMPLATES.INFRA_SAAS]: buildSteps(
        FORM_STEPS.INFRA,
        FORM_STEPS.SAAS,
        FORM_STEPS.PAYMENT_DETAILS,
        FORM_STEPS.SPOC_DETAILS
    ),
    [DEAL_TEMPLATES.INFRA]: buildSteps(FORM_STEPS.INFRA, FORM_STEPS.PAYMENT_DETAILS, FORM_STEPS.SPOC_DETAILS),
    [DEAL_TEMPLATES.SOLO_SURVEY]: [
        FORM_STEPS.DEAL_SOW,
        FORM_STEPS.ACCOUNT_N_LOCATION,
        FORM_STEPS.SURVEY,
        FORM_STEPS.SPOC_DETAILS,
    ],
    [DEAL_TEMPLATES.SOLO_LOGISTICS]: [
        FORM_STEPS.DEAL_SOW,
        FORM_STEPS.ACCOUNT_N_LOCATION,
        FORM_STEPS.MATERIAL_DISPATCH,
        FORM_STEPS.PAYMENT_DETAILS,
    ],
    [DEAL_TEMPLATES.DEMOLITION]: [
        FORM_STEPS.DEAL_SOW,
        FORM_STEPS.ACCOUNT_N_LOCATION,
        FORM_STEPS.PROJECT_DETAILS,
        FORM_STEPS.CHARGER_DETAILS,
        FORM_STEPS.PAYMENT_DETAILS,
        FORM_STEPS.SPOC_DETAILS,
    ],
    [DEAL_TEMPLATES.EV_INFRA]: [
        FORM_STEPS.DEAL_SOW,
        FORM_STEPS.ACCOUNT_N_LOCATION,
        FORM_STEPS.PROJECT_DETAILS,
    ],
}

/**
 * Get the steps for a given deal template and deal type
 * @param template - The deal template
 * @param dealType - The deal type
 * @returns The steps for the given deal template and deal type
 */
export function getStepsForTemplate(template: DEAL_TEMPLATES, dealType: DEAL_TYPE): StepDefinition[] {
    // If template not configured, return base steps mapped to FormSteps
    const configuredSteps = STEPS_CONFIG[template]
    if (!configuredSteps) {
        return BASE_STEPS.map(stepKey => FormSteps[stepKey])
    }

    // Filter steps if dealType is CPO, otherwise use all configured steps
    const filteredSteps =
        dealType === DEAL_TYPE.CPO
            ? configuredSteps.filter(stepKey => stepKey !== FORM_STEPS.PAYMENT_DETAILS)
            : configuredSteps

    // Map to StepDefinition objects from FormSteps
    return filteredSteps.map(stepKey => FormSteps[stepKey])
}

/**
 * Get the steps configuration based on the deal template and deal type
 * @param template - The deal template
 * @param dealType - The deal type
 * @returns The steps configuration
 */
export function getStepsConfig(template: DEAL_TEMPLATES, dealType: DEAL_TYPE): FORM_STEPS[] {
    const configuredSteps = STEPS_CONFIG[template]
    if (!configuredSteps) {
        return BASE_STEPS
    }

    const filteredConfig = dealType === DEAL_TYPE.CPO ? configuredSteps.filter(stepKey => stepKey !== FORM_STEPS.PAYMENT_DETAILS) : configuredSteps
    return filteredConfig
}

/**
 * Get the current component for a given step
 * @param step - The step to get the component for
 * @returns The component for the given step
 */
export function currentComponent(step: FORM_STEPS): React.ComponentType<any> {
    return FormSteps[step].component
}

export function getTemplateFromSow(sow: SCOPE_OF_WORK[]): DEAL_TEMPLATES | null {
    for (const template in SOW_MAPPING) {
        if (
            SOW_MAPPING[template as unknown as DEAL_TEMPLATES].every(sowItem => sow.includes(sowItem)) &&
            sow.every(sowItem => SOW_MAPPING[template as unknown as DEAL_TEMPLATES].includes(sowItem))
        ) {
            return template as unknown as DEAL_TEMPLATES
        }
    }
    return null
}